/**
 * Macro Store - Zustand store for Macro and Exclusion state.
 * 
 * Manages:
 * - Macro goals (calories, protein, carbs, fat)
 * - Food exclusions/allergies
 * - Temporary macro editing state
 * - Macro presets and animated transitions
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Macros, MacroPreset } from '../types';
import { updateMacros as apiUpdateMacros, updateExclusions as apiUpdateExclusions, fetchUserProfile } from '../services/api';

interface MacroState {
    macros: Macros;
    tempMacros: Partial<Macros>;
    exclusions: string[];
    newExclusion: string;
    activePreset: string | null;
    isLoading: boolean;
    error: string | null;
}

interface MacroActions {
    setMacros: (macros: Macros) => void;
    setTempMacros: (tempMacros: Partial<Macros>) => void;
    setExclusions: (exclusions: string[]) => void;
    setNewExclusion: (val: string) => void;
    setActivePreset: (presetName: string | null) => void;

    loadMacrosAndExclusions: (token: string) => Promise<void>;
    updateMacros: (token: string, payload: Macros) => Promise<void>;
    addExclusion: (token: string, item: string) => Promise<void>;
    removeExclusion: (token: string, item: string) => Promise<void>;

    // Auto-update helpers (often called from AI command center)
    autoUpdateMacros: (token: string, newMacros: Partial<Macros> & { p?: number, c?: number, f?: number }) => Promise<void>;
    autoUpdateExclusions: (token: string, updates: { add?: string[], remove?: string[] }) => Promise<void>;

    handlePresetSelect: (preset: MacroPreset, token?: string) => void;
    reset: () => void;
}

export type MacroStore = MacroState & MacroActions;

const initialState: MacroState = {
    macros: { calories: 2000, protein: 150, carbs: 200, fat: 65 },
    tempMacros: {},
    exclusions: [],
    newExclusion: '',
    activePreset: null,
    isLoading: false,
    error: null,
};

export const useMacroStore = create<MacroStore>()(
    devtools(
        (set, get) => ({
            ...initialState,

            setMacros: (macros) => set({ macros }),
            setTempMacros: (tempMacros) => set({ tempMacros }),
            setExclusions: (exclusions) => set({ exclusions }),
            setNewExclusion: (newExclusion) => set({ newExclusion }),
            setActivePreset: (activePreset) => set({ activePreset }),

            loadMacrosAndExclusions: async (token) => {
                set({ isLoading: true, error: null });
                try {
                    const profile = await fetchUserProfile(token);
                    set({
                        macros: {
                            calories: profile.calories || 2000,
                            protein: profile.protein || 150,
                            carbs: profile.carbs || 200,
                            fat: profile.fat || 65
                        },
                        exclusions: profile.exclusions || [],
                        isLoading: false
                    });
                } catch (err) {
                    set({ error: 'Failed to load macros', isLoading: false });
                    console.error('Failed to load macros:', err);
                }
            },

            updateMacros: async (token, payload) => {
                set({ isLoading: true });
                try {
                    await apiUpdateMacros(token, payload);
                    set({
                        macros: payload,
                        tempMacros: {},
                        activePreset: null,
                        isLoading: false
                    });
                } catch (err) {
                    set({ isLoading: false });
                    throw err;
                }
            },

            addExclusion: async (token, item) => {
                const { exclusions } = get();
                const normalized = item.trim().toLowerCase();
                if (!normalized || exclusions.includes(normalized)) return;

                const updated = [...exclusions, normalized];
                set({ exclusions: updated, newExclusion: '' });

                try {
                    await apiUpdateExclusions(token, updated);
                } catch (err) {
                    console.error('Failed to update exclusions:', err);
                    set({ exclusions }); // Rollback
                    throw err;
                }
            },

            removeExclusion: async (token, item) => {
                const { exclusions } = get();
                const updated = exclusions.filter(e => e !== item);
                set({ exclusions: updated });

                try {
                    await apiUpdateExclusions(token, updated);
                } catch (err) {
                    console.error('Failed to remove exclusion:', err);
                    set({ exclusions }); // Rollback
                    throw err;
                }
            },

            autoUpdateMacros: async (token, newMacros) => {
                const { macros } = get();
                const mappedMacros = {
                    calories: newMacros.calories || macros.calories,
                    protein: newMacros.protein || newMacros.p || macros.protein,
                    carbs: newMacros.carbs || newMacros.c || macros.carbs,
                    fat: newMacros.fat || newMacros.f || macros.fat
                };

                try {
                    await apiUpdateMacros(token, mappedMacros);
                    set({ macros: mappedMacros });
                } catch (err) {
                    console.error('Auto macro update failed:', err);
                    throw err;
                }
            },

            autoUpdateExclusions: async (token, updates) => {
                const { exclusions } = get();
                const current = new Set(exclusions);
                if (updates.add) updates.add.forEach(item => current.add(item.toLowerCase()));
                if (updates.remove) updates.remove.forEach(item => current.delete(item.toLowerCase()));

                const updated = Array.from(current);
                set({ exclusions: updated });

                try {
                    await apiUpdateExclusions(token, updated);
                } catch (err) {
                    console.error('Auto exclusion update failed:', err);
                    set({ exclusions }); // Rollback
                    throw err;
                }
            },

            handlePresetSelect: (preset, token) => {
                const { tempMacros, macros } = get();
                set({ activePreset: preset.name });

                const calories = tempMacros.calories || macros.calories;
                const newMacros = {
                    calories,
                    protein: Math.round((calories * (preset.protein / 100)) / 4),
                    carbs: Math.round((calories * (preset.carbs / 100)) / 4),
                    fat: Math.round((calories * (preset.fat / 100)) / 9),
                };

                // Optimistic background sync if token provided
                if (token) {
                    apiUpdateMacros(token, newMacros).catch(err => {
                        console.error("Failed to persist preset macros:", err);
                    });
                }

                const steps = 20;
                const duration = 400;
                const interval = duration / steps;
                let currentStep = 0;
                const startValues = {
                    calories: tempMacros.calories || macros.calories,
                    protein: tempMacros.protein || macros.protein,
                    carbs: tempMacros.carbs || macros.carbs,
                    fat: tempMacros.fat || macros.fat,
                };

                const timer = setInterval(() => {
                    currentStep++;
                    const progress = currentStep / steps;

                    set({
                        tempMacros: {
                            calories,
                            protein: Math.round(startValues.protein + (newMacros.protein - startValues.protein) * progress),
                            carbs: Math.round(startValues.carbs + (newMacros.carbs - startValues.carbs) * progress),
                            fat: Math.round(startValues.fat + (newMacros.fat - startValues.fat) * progress),
                        }
                    });

                    if (currentStep >= steps) {
                        clearInterval(timer);
                        // Finalize the actual macros state after animation
                        set({ macros: newMacros, tempMacros: {} });
                    }
                }, interval);
            },

            reset: () => set(initialState),
        }),
        { name: 'macro-store' }
    )
);
